import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdFlight,
  MdInfo,
  MdSettings,
  MdImage,
  MdPictureAsPdf,
  MdDescription,
  MdPerson,
  MdBusiness,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const ConfirmStatusText: any = { 0: "Pending", 1: "Approved", 2: "Rejected", 3: "Expired" };
const ConfirmStatusColor: any = { 0: "#F59E0B", 1: "#16A34A", 2: "#DC2626", 3: "#6B7280" };

const Loader = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
    <div
      style={{
        width: 42,
        height: 42,
        border: "4px solid #CBD5E1",
        borderTopColor: "#2563EB",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
  </div>
);

const BoolBadge = ({ value }: any) => (
  <Badge
    label={value ? "Yes" : "No"}
    color={value ? "#16A34A" : "#DC2626"}
    bg={value ? "rgba(22,163,74,.15)" : "rgba(220,38,38,.15)"}
  />
);

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 200px 1fr",
      alignItems: "center",
      gap: 10,
      padding: "6px 0",
      borderBottom: "1px solid #E5E7EB",
      fontSize: 12,
    }}
  >
    <Icon size={24} color="#25b3ebff" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: 18,
      padding: 16,
      background: "#FFFFFFAA",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        background:
          "linear-gradient(135deg, #3B82F6 0%, #60A5FA 50%, #93C5FD 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: 4,
      }}
    >
      <Icon size={24} color="#ffffffff" />
      <strong style={{ fontSize: 15 }}>{title}</strong>
    </div>
    {children}
  </div>
);

const WetLeaseWantedDetailModal = ({ id, onClose }: any) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.wetLeaseWanted.getById(id).then((r: any) => {
      setData(r?.data?.data ?? r?.data);
      setLoading(false);
    });
  }, [id]);

  return (
    <Modal open fullScreen onClose={onClose}>
      {loading ? (
        <Loader />
      ) : (
        <div style={{ padding: 24 }}>
          <h1 style={{ fontSize: 22, marginBottom: 24 }}>{data.headline}</h1>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(420px,1fr))",
              gap: 20,
            }}
          >
            <Card icon={MdDescription} title="General">
              <InfoRow
                icon={MdDescription}
                label="Headline"
                value={data.headline}
              />
              <InfoRow
                icon={MdInfo}
                label="Description"
                value={data.description}
              />
              <InfoRow
                icon={MdInfo}
                label="Confirm Status"
                value={
                  <Badge
                    label={(ConfirmStatusText as any)[data.confirmStatus]}
                    color={(ConfirmStatusColor as any)[data.confirmStatus]}
                  />
                }
              />
              <InfoRow
                icon={MdSettings}
                label="Is Active"
                value={<BoolBadge value={data.isActive} />}
              />
            </Card>

            <Card icon={MdInfo} title="Lease Details">
              <InfoRow
                icon={MdPerson}
                label="Name Of Lessee"
                value={data.nameOfLessee}
              />
              <InfoRow
                icon={MdInfo}
                label="Period"
                value={`${new Date(
                  data.startDate
                ).toLocaleDateString()} - ${new Date(
                  data.endDate
                ).toLocaleDateString()}`}
              />
              <InfoRow
                icon={MdSettings}
                label="MM Block Hour Guarantee"
                value={data.mmBlockHourGuarantee}
              />
            </Card>

            <Card icon={MdFlight} title="Aircraft">
              <InfoRow
                icon={MdFlight}
                label="Aircraft Type"
                value={data.aircraftType?.name}
              />
              <InfoRow
                icon={MdFlight}
                label="Aircraft Model"
                value={data.aircraftTypeModel?.name}
              />
              <InfoRow
                icon={MdSettings}
                label="Model Detail"
                value={data.aircraftTypeModelDetail}
              />
              <InfoRow
                icon={MdInfo}
                label="Any Type"
                value={<BoolBadge value={data.anyType} />}
              />
              <InfoRow icon={MdSettings} label="YOM" value={data.yom} />
              <InfoRow
                icon={MdSettings}
                label="Capacity"
                value={data.capacity}
              />
            </Card>

            <Card icon={MdSettings} title="Engine">
              <InfoRow
                icon={MdSettings}
                label="Engine Type"
                value={data.engineType?.code}
              />
              <InfoRow
                icon={MdSettings}
                label="Engine Model Detail"
                value={data.engineModelDetail}
              />
            </Card>

            <Card icon={MdSettings} title="Operations">
              <InfoRow
                icon={MdCheck}
                label="Backup Aircraft Wanted"
                value={<BoolBadge value={data.backupAircraftWanted} />}
              />
              <InfoRow
                icon={MdCheck}
                label="Set Of Crew Wanted"
                value={<BoolBadge value={data.setOfCrewWanted} />}
              />
              <InfoRow
                icon={MdInfo}
                label="Airport"
                value={
                  data.airport
                    ? `${data.airport.name} (${data.airport.codeIATA})`
                    : "-"
                }
              />
            </Card>

            <Card icon={MdPerson} title="User">
              <InfoRow
                icon={MdPerson}
                label="Username"
                value={data.user?.username}
              />
              <InfoRow
                icon={MdPerson}
                label="Full Name"
                value={`${data.user?.firstName ?? ""} ${
                  data.user?.lastName ?? ""
                }`}
              />
              <InfoRow icon={MdInfo} label="Email" value={data.user?.email} />
              <InfoRow
                icon={MdInfo}
                label="Phone"
                value={data.user?.phoneNumber}
              />
              <InfoRow
                icon={MdInfo}
                label="Office Phone"
                value={data.user?.officePhoneNumber}
              />
            </Card>

            <Card icon={MdBusiness} title="Company">
              <InfoRow
                icon={MdBusiness}
                label="Company Name"
                value={data.company?.companyName}
              />
              <InfoRow
                icon={MdInfo}
                label="Tax Number"
                value={data.company?.taxNumber}
              />
              <InfoRow
                icon={MdInfo}
                label="Tax Office"
                value={data.company?.taxOffice}
              />
              <InfoRow
                icon={MdInfo}
                label="Address"
                value={data.company?.address}
              />
              <InfoRow
                icon={MdInfo}
                label="Location"
                value={
                  data.company
                    ? `${data.company.city} / ${data.company.country}`
                    : "-"
                }
              />
              <InfoRow
                icon={MdInfo}
                label="Phone"
                value={data.company?.phone}
              />
              <InfoRow
                icon={MdInfo}
                label="Email"
                value={data.company?.email}
              />
            </Card>
            <Card icon={MdInfo} title="Location">
              <InfoRow
                icon={MdInfo}
                label="Continent"
                value={data.location?.continent?.name ?? "-"}
              />
              <InfoRow
                icon={MdInfo}
                label="Country"
                value={data.location?.country?.name ?? "-"}
              />
              <InfoRow
                icon={MdInfo}
                label="City"
                value={data.location?.city?.name ?? "-"}
              />
              <InfoRow
                icon={MdFlight}
                label="Airport"
                value={
                  data.location?.airport
                    ? `${data.location.airport.name} (${data.location.airport.codeIATA})`
                    : "-"
                }
              />
            </Card>
          </div>

          <Card icon={MdImage} title="Images">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {(data.images ?? []).length === 0 && <span>-</span>}
              {(data.images ?? []).map((m: any) => (
                <img
                  key={m.id}
                  src={m.url}
                  onClick={() => setPreview(m.url)}
                  style={{
                    width: 140,
                    height: 140,
                    objectFit: "cover",
                    borderRadius: 14,
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </Card>

          <Card icon={MdPictureAsPdf} title="Documents">
            <div style={{ display: "flex", gap: 16 }}>
              {(data.documents ?? []).length === 0 && <span>-</span>}
              {(data.documents ?? []).map((m: any) => (
                <div
                  key={m.id}
                  onClick={() => setPreview(m.url)}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 14,
                    border: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <MdPictureAsPdf size={36} />
                </div>
              ))}
            </div>
          </Card>

          {preview && (
            <Modal open onClose={() => setPreview(null)}>
              {preview.endsWith(".pdf") ? (
                <iframe
                  src={preview}
                  style={{ width: "100%", height: "80vh" }}
                />
              ) : (
                <img src={preview} style={{ maxWidth: "100%" }} />
              )}
            </Modal>
          )}
        </div>
      )}
    </Modal>
  );
};

const ConfirmStatusModal = ({ row, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [status, setStatus] = useState(row.confirmStatus);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await api.wetLeaseWanted.confirmStatus(row.id, status);
    setSaving(false);
    onClose();
    onSaved();
  };

  return (
    <Modal open onClose={onClose}>
      <div style={S.modalTitle}>Confirm Status</div>
      <select
        value={status}
        onChange={(e) => setStatus(+e.target.value)}
        style={{ width: "100%", height: 36, marginBottom: 16 }}
      >
        <option value={0}>Pending</option>
        <option value={1}>Approved</option>
        <option value={2}>Rejected</option>
      </select>

      <div style={{ display: "flex", gap: 8 }}>
        <Button icon={MdCheck} loading={saving} onClick={save}>
          Confirm
        </Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

export const WetLeaseWantedForms = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [statusRow, setStatusRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await api.wetLeaseWanted.getAll();
    setData(r?.data?.data ?? r?.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo<any[]>(
    () => [
      {
        key: "aircraftType",
        title: "AirCraft",
        type: "text",
        accessor: (r: any) => r.aircraftType?.name,
      },
      {
        key: "aircraftTypeModel",
        title: "AirCraft Model",
        type: "text",
        accessor: (r: any) => r.aircraftTypeModel?.name,
      },
      {
        key: "description",
        title: "Description",
        type: "text",
        accessor: (r: any) => r.description,
      },
      {
        key: "airport",
        title: "AirPort",
        type: "text",
        accessor: (r: any) => r.airport?.codeIATA,
      },
      {
        key: "company",
        title: "Company",
        type: "text",
        accessor: (r: any) => r.company?.companyName,
      },
      { key: "capacity", title: "Capacity", type: "number", accessor: (r: any) => r.capacity },
      { key: "lessee", title: "Lessee", type: "text", accessor: (r: any) => r.nameOfLessee },

      {
        key: "confirmStatus",
        title: "Status",
        type: "number",
        accessor: (r: any) => r.confirmStatus,
        render: (_: any, v: any) => (
          <Badge
            label={(ConfirmStatusText as any)[v]}
            color={(ConfirmStatusColor as any)[v]}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        filterable: false,
        render: (r: any) => (
          <GroupButton
            items={[
              {
                key: "detail",
                icon: MdVisibility,
                color: "#2563EB",
                onClick: () => setDetailId(r.id),
              },
              {
                key: "confirm",
                icon: MdCheck,
                color: "#F59E0B",
                onClick: () => setStatusRow(r),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={S.title}>Aircraft Wet Lease Wanted Forms</div>
        <div style={S.subTitle}>Manage system </div>
      </div>
      <Table loading={loading} columns={columns} data={data} />

      {detailId && (
        <WetLeaseWantedDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {statusRow && (
        <ConfirmStatusModal
          row={statusRow}
          onClose={() => setStatusRow(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
